import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import { AuthContext } from './AuthContext';
import axios from 'axios';
import notificationHelper from '../utils/notificationHelper';

export const ChatContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

export const ChatProvider = ({ children }) => {
  const { token, user } = useContext(AuthContext);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState({}); // { conversationId: [messages] }
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [userStatuses, setUserStatuses] = useState(new Map()); // Map<userId, 'online'|'away'|'offline'>
  const [unreadCounts, setUnreadCounts] = useState({}); // { conversationId: count }
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);
  const [chatWidth, setChatWidth] = useState(400); // Chat-Breite in Pixel

  // Track last marked conversation to prevent duplicate mark-as-read calls
  const lastMarkedConvRef = useRef(null);

  // Web Worker for background heartbeats (not throttled by browser)
  const heartbeatWorkerRef = useRef(null);

  // Track socket initialization to prevent duplicate connections
  const socketInitializedRef = useRef(false);
  const currentUserIdRef = useRef(null);

  // Initialize Socket.IO connection
  useEffect(() => {
    // Ansichtsmodus (Impersonation): keine Socket-Verbindung – sonst erschiene
    // der angesehene User als online und der Admin läse fremde Nachrichten mit.
    if (user?.impersonated_by) {
      console.log('[CHAT] Ansichtsmodus aktiv – Socket wird nicht verbunden');
      return;
    }
    // Chat ist nur für interne Accounts; der Server lehnt Kunden-Sockets ab.
    if (user?.role === 'client') {
      return;
    }
    if (token && user) {
      // Prevent duplicate socket creation for the same user
      if (socketInitializedRef.current && currentUserIdRef.current === user.id) {
        console.log('[CHAT] Socket already initialized for user:', user.id, '- skipping');
        return;
      }

      console.log('[CHAT] Initializing socket connection...', { user: user.id, timestamp: new Date().toISOString() });
      socketInitializedRef.current = true;
      currentUserIdRef.current = user.id;
      const newSocket = io(BACKEND_URL, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,       // Start with 1 second
        reconnectionDelayMax: 10000,   // Max 10 seconds between attempts
        reconnectionAttempts: 15,      // More attempts for better resilience during calls
        timeout: 20000,                // 20 second connection timeout
        pingTimeout: 30000,            // 30 seconds before considering connection dead
        pingInterval: 15000            // Ping every 15 seconds to keep connection alive
      });

      // Catch-all listener for debugging - logs ALL events
      newSocket.onAny((eventName, ...args) => {
        console.log('[CHAT] Socket event received:', eventName, args);
      });

      newSocket.on('connect', () => {
        setIsConnected(true);
        console.log('[CHAT] Socket connected! ID:', newSocket.id, 'Connected:', newSocket.connected);
      });

      newSocket.on('disconnect', (reason) => {
        setIsConnected(false);
        console.log('[CHAT] Socket disconnected. Reason:', reason);
      });

      newSocket.on('connect_error', (error) => {
        console.error('[CHAT] Socket connection error:', error);
      });

      newSocket.on('error', (error) => {
        console.error('[CHAT] Socket error:', error);
      });

      newSocket.on('reconnect_attempt', (attemptNumber) => {
        console.log('[CHAT] Socket reconnection attempt:', attemptNumber);
      });

      newSocket.on('reconnect', (attemptNumber) => {
        console.log('[CHAT] Socket reconnected after', attemptNumber, 'attempts');
      });

      newSocket.on('reconnect_error', (error) => {
        console.error('[CHAT] Socket reconnection error:', error);
      });

      newSocket.on('reconnect_failed', () => {
        console.error('[CHAT] Socket reconnection failed - all attempts exhausted');
      });

      // Listen for initial online users list (legacy format for backward compat)
      newSocket.on('online_users_list', (data) => {
        console.log('[CHAT] Online users list received:', data);
        setOnlineUsers(new Set(data.user_ids));
      });

      // Listen for full users status list (new format with online/away/offline)
      newSocket.on('users_status_list', (data) => {
        console.log('[CHAT] Users status list received:', data);
        const statusMap = new Map();
        data.users.forEach(u => {
          statusMap.set(u.user_id, u.status);
        });
        setUserStatuses(statusMap);
      });

      // Listen for new messages
      newSocket.on('new_message', (data) => {
        console.log('[CHAT] New message received:', data);
        handleNewMessage(data);
      });

      // Listen for message sent confirmation
      newSocket.on('message_sent', (data) => {
        console.log('[CHAT] Message sent confirmation:', data);
        handleNewMessage(data);
      });

      // Listen for user status changes (supports online/away/offline)
      newSocket.on('user_status_change', (data) => {
        console.log('[CHAT] *** USER STATUS CHANGE RECEIVED ***:', data);

        // Update new userStatuses Map (online/away/offline)
        const status = data.status || (data.is_online ? 'online' : (data.is_away ? 'away' : 'offline'));
        console.log('[CHAT] Setting user', data.user_id, 'status to', status);

        setUserStatuses(prev => {
          const next = new Map(prev);
          next.set(data.user_id, status);
          return next;
        });

        // Also update legacy onlineUsers Set for backward compatibility
        if (data.is_online) {
          console.log('[CHAT] Setting user', data.user_id, 'ONLINE (legacy)');
          setOnlineUsers(prev => {
            const next = new Set(prev);
            next.add(data.user_id);
            return next;
          });
        } else {
          console.log('[CHAT] Setting user', data.user_id, 'OFFLINE/AWAY (legacy)');
          setOnlineUsers(prev => {
            const next = new Set(prev);
            next.delete(data.user_id);
            return next;
          });

          // Update last_seen_at in allUsers
          if (data.last_seen_at) {
            setAllUsers(prev => prev.map(u =>
              u.id === data.user_id ? { ...u, last_seen_at: data.last_seen_at } : u
            ));

            // Also update last_seen_at in conversations
            setConversations(prev => prev.map(conv => {
              if (conv.other_user && conv.other_user.id === data.user_id) {
                return {
                  ...conv,
                  other_user: {
                    ...conv.other_user,
                    last_seen_at: data.last_seen_at
                  }
                };
              }
              return conv;
            }));

            // Also update activeConversation if it's with this user
            setActiveConversation(prev => {
              if (prev && prev.other_user && prev.other_user.id === data.user_id) {
                return {
                  ...prev,
                  other_user: {
                    ...prev.other_user,
                    last_seen_at: data.last_seen_at
                  }
                };
              }
              return prev;
            });
          }
        }
      });

      // Listen for typing indicators
      newSocket.on('user_typing', (data) => {
        console.log('[CHAT] User typing:', data);
        setTypingUsers(prev => new Set([...prev, data.user_id]));
        setTimeout(() => {
          setTypingUsers(prev => {
            const next = new Set(prev);
            next.delete(data.user_id);
            return next;
          });
        }, 3000);
      });

      // Listen for read receipts
      newSocket.on('message_read_receipt', (data) => {
        console.log('[CHAT] Message read receipt:', data);
        updateMessageReadStatus(data.message_id, data.read_at);
      });

      // Listen for message edits
      newSocket.on('message_edited', (data) => {
        console.log('[CHAT] Message edited:', data);
        updateMessage(data);
      });

      // Listen for message deletes
      newSocket.on('message_deleted', (data) => {
        console.log('[CHAT] Message deleted:', data);
        updateMessage(data);
      });

      // Listen for reactions
      newSocket.on('reaction_added', (data) => {
        console.log('[CHAT] Reaction added:', data);
        if (data.message) {
          updateMessage(data.message);
        }
      });

      newSocket.on('reaction_removed', (data) => {
        console.log('[CHAT] Reaction removed:', data);
        if (data.message) {
          updateMessage(data.message);
        }
      });

      setSocket(newSocket);
      console.log('[CHAT] Socket stored in state, listeners registered');

      return () => {
        console.log('[CHAT] Closing socket connection');
        socketInitializedRef.current = false;
        currentUserIdRef.current = null;
        newSocket.close();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.id]); // Only react to user.id changes, not the entire user object

  // Handle new message
  const handleNewMessage = useCallback((data) => {
    const convId = data.conversation_id;

    setMessages(prev => ({
      ...prev,
      [convId]: [...(prev[convId] || []), data]
    }));

    // Update conversation list locally - DON'T fetch from backend!
    // Fetching would overwrite our local unread count management
    setConversations(prev => {
      const exists = prev.find(c => c.id === convId);

      if (exists) {
        // Update existing conversation
        const updated = prev.map(conv => {
          if (conv.id === convId) {
            return {
              ...conv,
              last_message_at: new Date().toISOString(),
              last_message: {
                content: data.content,
                sender_id: data.sender_id,
                created_at: data.created_at
              }
            };
          }
          return conv;
        });
        // Sort by last_message_at
        return updated.sort((a, b) => {
          const timeA = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
          const timeB = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
          return timeB - timeA;
        });
      } else {
        // Conversation doesn't exist yet - need to fetch it from backend
        if (token) {
          axios.get(`${BACKEND_URL}/api/chat/conversations`, {
            headers: { Authorization: `Bearer ${token}` }
          })
            .then(response => {
              setConversations(response.data);
              // Don't update unread counts - they're managed locally
            })
            .catch(error => console.error('Failed to fetch conversations:', error));
        }
        return prev;
      }
    });

    // Determine if message should be marked as read immediately
    // Only mark as read if: active conversation AND browser visible AND chat open
    const isActiveAndVisible =
      activeConversation?.id === convId &&
      isPageVisible &&
      isChatOpen;

    // Update unread count if message is from other user AND NOT immediately marked as read
    if (data.sender_id !== user?.id && !isActiveAndVisible) {
      setUnreadCounts(prev => ({
        ...prev,
        [convId]: (prev[convId] || 0) + 1
      }));
    }

    // Show notifications if message is from other user
    if (data.sender_id !== user?.id) {
      const shouldNotify = !isActiveAndVisible; // Same logic as unread count!

      console.log('[NOTIFICATIONS] Check:', {
        shouldNotify,
        documentHidden: document.hidden,
        isChatOpen,
        senderId: data.sender_id,
        currentUserId: user?.id
      });

      if (shouldNotify) {
        // Desktop Notification
        const desktopEnabled = localStorage.getItem('chat_desktop_notifications') === 'true';
        const permissionStatus = notificationHelper.getPermissionStatus();

        console.log('[NOTIFICATIONS] Desktop check:', {
          desktopEnabled,
          permissionStatus,
          senderData: data.sender
        });

        if (desktopEnabled && permissionStatus === 'granted') {
          // Get sender info directly from message data
          const senderName = data.sender?.full_name || data.sender?.username || 'Unbekannt';

          console.log('[NOTIFICATIONS] Showing notification for:', senderName);

          notificationHelper.showChatNotification({
            senderName,
            message: data.content,
            onClick: () => {
              console.log('[NOTIFICATIONS] Notification clicked');
              // Open chat sidebar and find the conversation
              setIsChatOpen(true);

              // Find conversation in local state and set it active
              const conv = conversations.find(c => c.id === convId);
              if (conv) {
                setActiveConversation(conv);
                // Messages will be fetched automatically when conversation becomes active
              }
            }
          });
        }

        // Sound Notification
        const soundEnabled = localStorage.getItem('chat_sound_enabled') !== 'false';
        console.log('[NOTIFICATIONS] Sound enabled:', soundEnabled);
        if (soundEnabled) {
          playMessageSound();
        }
      }
    }
  }, [activeConversation, user, token, isChatOpen, isPageVisible, setIsChatOpen, setActiveConversation, conversations]);

  // Update message read status
  const updateMessageReadStatus = useCallback((messageId, readAt) => {
    setMessages(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(convId => {
        updated[convId] = updated[convId].map(msg =>
          msg.id === messageId ? { ...msg, is_read: true, read_at: readAt } : msg
        );
      });
      return updated;
    });
  }, []);

  // Update message (for edit/delete)
  const updateMessage = useCallback((updatedMessage) => {
    setMessages(prev => {
      const updated = { ...prev };
      const convId = updatedMessage.conversation_id;
      if (updated[convId]) {
        updated[convId] = updated[convId].map(msg =>
          msg.id === updatedMessage.id ? { ...msg, ...updatedMessage } : msg
        );
      }
      return updated;
    });
  }, []);

  // Fetch conversations
  const fetchConversations = useCallback(async (updateUnreadCounts = true) => {
    if (!token) return;

    try {
      const response = await axios.get(`${BACKEND_URL}/api/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);

      // Only update unread counts if explicitly requested (e.g., on initial load)
      // Don't update them when fetching due to new messages - those are managed locally!
      if (updateUnreadCounts) {
        const counts = {};
        response.data.forEach(conv => {
          if (conv.unread_count > 0) {
            counts[conv.id] = conv.unread_count;
          }
        });
        setUnreadCounts(counts);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    }
  }, [token]);

  // Fetch messages for a conversation
  const fetchMessages = useCallback(async (conversationId) => {
    if (!token) return;

    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/chat/conversations/${conversationId}/messages`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setMessages(prev => ({
        ...prev,
        [conversationId]: response.data
      }));
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  }, [token]);

  // Fetch all users
  const fetchUsers = useCallback(async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${BACKEND_URL}/api/chat/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data);

      // Update online users
      const online = new Set(
        response.data.filter(u => u.is_online).map(u => u.id)
      );
      setOnlineUsers(online);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [token]);

  // Start conversation with a user
  const startConversation = useCallback(async (userId) => {
    if (!token) return null;

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/chat/conversations/start/${userId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const conv = response.data;
      setActiveConversation(conv);

      // Fetch messages for this conversation
      await fetchMessages(conv.id);

      // Add to conversations if not exists
      setConversations(prev => {
        if (prev.find(c => c.id === conv.id)) {
          return prev;
        }
        return [conv, ...prev];
      });

      return conv;
    } catch (error) {
      console.error('Failed to start conversation:', error);
      return null;
    }
  }, [token, fetchMessages]);

  // Upload file for chat
  const uploadFile = useCallback(async (file) => {
    if (!token) return null;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/chat/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      console.log('[CHAT] File uploaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('[CHAT] File upload failed:', error);
      throw error;
    }
  }, [token]);

  // Send message (with optional attachment)
  const sendMessage = useCallback((recipientId, content, attachment = null) => {
    console.log('[CHAT] sendMessage called:', { recipientId, content, attachment, socket: !!socket, isConnected });
    if (socket && isConnected) {
      console.log('[CHAT] Emitting send_message event');
      const messageData = {
        recipient_id: recipientId,
        content: content || ''
      };

      if (attachment) {
        messageData.attachment = attachment;
      }

      socket.emit('send_message', messageData);
    } else {
      console.error('[CHAT] Cannot send message - socket not connected!', { socket: !!socket, isConnected });
    }
  }, [socket, isConnected]);

  // Send typing indicator
  const sendTyping = useCallback((recipientId) => {
    if (socket && isConnected) {
      socket.emit('typing', { recipient_id: recipientId });
    }
  }, [socket, isConnected]);

  // Mark message as read
  const markAsRead = useCallback((messageId) => {
    if (socket && isConnected) {
      socket.emit('message_read', { message_id: messageId });
    }
  }, [socket, isConnected]);

  // Mark entire conversation as read
  const markConversationAsRead = useCallback((conversationId) => {
    if (socket && isConnected) {
      socket.emit('mark_conversation_read', { conversation_id: conversationId });

      // Update local unread count
      setUnreadCounts(prev => {
        const updated = { ...prev };
        delete updated[conversationId];
        return updated;
      });
    }
  }, [socket, isConnected]);

  // Edit message
  const editMessage = useCallback((messageId, newContent) => {
    console.log('[CHAT] editMessage called:', { messageId, newContent, socket: !!socket, isConnected });
    if (socket && isConnected) {
      console.log('[CHAT] Emitting edit_message event');
      socket.emit('edit_message', {
        message_id: messageId,
        new_content: newContent
      });
    } else {
      console.error('[CHAT] Cannot edit message - socket not connected!', { socket: !!socket, isConnected });
    }
  }, [socket, isConnected]);

  // Delete message
  const deleteMessage = useCallback((messageId) => {
    console.log('[CHAT] deleteMessage called:', { messageId, socket: !!socket, isConnected });
    if (socket && isConnected) {
      console.log('[CHAT] Emitting delete_message event');
      socket.emit('delete_message', {
        message_id: messageId
      });
    } else {
      console.error('[CHAT] Cannot delete message - socket not connected!', { socket: !!socket, isConnected });
    }
  }, [socket, isConnected]);

  // Add reaction to message
  const addReaction = useCallback((messageId, emoji) => {
    console.log('[CHAT] addReaction called:', { messageId, emoji, socket: !!socket, isConnected });
    if (socket && isConnected) {
      console.log('[CHAT] Emitting add_reaction event');
      socket.emit('add_reaction', {
        message_id: messageId,
        emoji: emoji
      });
    } else {
      console.error('[CHAT] Cannot add reaction - socket not connected!', { socket: !!socket, isConnected });
    }
  }, [socket, isConnected]);

  // Play message sound
  const playMessageSound = () => {
    try {
      const audio = new Audio('/sounds/message.mp3');
      audio.volume = 0.5;

      // Try to play, handle autoplay policy
      const playPromise = audio.play();

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('[NOTIFICATIONS] Sound played successfully');
          })
          .catch(error => {
            // Autoplay was prevented - this is normal for most browsers
            console.warn('[NOTIFICATIONS] Sound autoplay prevented:', error.name, error.message);
            console.warn('[NOTIFICATIONS] User must interact with the page first to enable sound');
          });
      }
    } catch (e) {
      console.error('[NOTIFICATIONS] Sound error:', e);
    }
  };

  // Load initial data
  useEffect(() => {
    if (token && user && user.role !== 'client') {
      fetchConversations();
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user]);

  // Listen for visibility changes and update state
  // Also handle reconnection and status refresh when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      const visible = !document.hidden;
      console.log('[CHAT] Visibility changed, visible:', visible);
      setIsPageVisible(visible);

      // When tab becomes visible again, ensure socket is connected and refresh online users
      if (visible && socket) {
        if (!socket.connected) {
          console.log('[CHAT] Tab visible, socket disconnected - reconnecting...');
          socket.connect();
        } else {
          // Socket is connected, but online users list might be stale
          // Request fresh status from server
          console.log('[CHAT] Tab visible - requesting fresh online users list');
          socket.emit('request_online_users');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [socket]);

  // Web Worker for background heartbeats
  // This worker is NOT throttled by browsers when tab is in background
  useEffect(() => {
    if (!token || !socket) return;

    try {
      // Create worker
      heartbeatWorkerRef.current = new Worker('/workers/heartbeatWorker.js');

      heartbeatWorkerRef.current.onmessage = (e) => {
        if (e.data.type === 'heartbeat') {
          if (socket.connected) {
            // Socket connected - send heartbeat to keep status alive
            socket.emit('heartbeat');
            console.log('[CHAT] Heartbeat sent via Worker');
          } else {
            // Socket disconnected - try to reconnect!
            // This is crucial for background tabs where socket gets throttled
            console.log('[CHAT] Heartbeat: Socket disconnected, attempting reconnect...');
            socket.connect();
          }
        }
      };

      heartbeatWorkerRef.current.onerror = (error) => {
        console.error('[CHAT] Heartbeat worker error:', error);
      };

      // Start the worker
      heartbeatWorkerRef.current.postMessage({ type: 'start' });
      console.log('[CHAT] Heartbeat worker started');

      return () => {
        if (heartbeatWorkerRef.current) {
          heartbeatWorkerRef.current.postMessage({ type: 'stop' });
          heartbeatWorkerRef.current.terminate();
          heartbeatWorkerRef.current = null;
          console.log('[CHAT] Heartbeat worker stopped');
        }
      };
    } catch (error) {
      console.error('[CHAT] Failed to create heartbeat worker:', error);
    }
  }, [token, socket]);

  // Unified mark-as-read logic
  // Marks messages as read when: activeConversation exists, browser visible, chat open
  useEffect(() => {
    if (!activeConversation || !messages[activeConversation.id] || !user || !socket || !isConnected) {
      return;
    }

    // Only mark as read if ALL conditions are met:
    // 1. Browser is visible (isPageVisible state)
    // 2. Chat sidebar is open (isChatOpen state)
    const shouldMarkAsRead = isPageVisible && isChatOpen;

    if (!shouldMarkAsRead) {
      console.log('[CHAT] Not marking as read:', { visible: isPageVisible, chatOpen: isChatOpen });
      return;
    }

    const convId = activeConversation.id;
    const convMessages = messages[convId];
    const unreadMessages = convMessages.filter(
      msg => !msg.is_read && msg.sender_id !== user.id
    );

    // Only mark if there are unread messages
    if (unreadMessages.length === 0) {
      return;
    }

    // Create a unique key for current unread state to prevent duplicates
    const unreadKey = `${convId}-${unreadMessages.map(m => m.id).join(',')}`;

    if (lastMarkedConvRef.current === unreadKey) {
      return; // Already marked this exact set
    }

    console.log('[CHAT] Marking conversation as read, unread messages:', unreadMessages.length);
    lastMarkedConvRef.current = unreadKey;

    // Use setTimeout to avoid race conditions
    const timer = setTimeout(() => {
      socket.emit('mark_conversation_read', { conversation_id: convId });
      setUnreadCounts(prev => {
        const updated = { ...prev };
        delete updated[convId];
        return updated;
      });
    }, 100);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversation?.id, messages, user?.id, socket, isConnected, isChatOpen, isPageVisible]);

  // Helper function to get user status (online/away/offline)
  const getUserStatus = useCallback((userId) => {
    return userStatuses.get(userId) || 'offline';
  }, [userStatuses]);

  const value = {
    socket,
    isConnected,
    conversations,
    activeConversation,
    setActiveConversation,
    messages,
    onlineUsers,
    userStatuses,  // New: Map<userId, 'online'|'away'|'offline'>
    getUserStatus, // Helper: getUserStatus(userId) => 'online'|'away'|'offline'
    unreadCounts,
    typingUsers,
    isChatOpen,
    setIsChatOpen,
    allUsers,
    chatWidth,
    setChatWidth,
    sendMessage,
    sendTyping,
    markAsRead,
    markConversationAsRead,
    editMessage,
    deleteMessage,
    addReaction,
    uploadFile,
    fetchConversations,
    fetchMessages,
    fetchUsers,
    startConversation
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
